import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
// Fontawesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faArrowRotateRight,
} from "@fortawesome/free-solid-svg-icons";
// Components
import TableLoader from "../../../components/TableLoader";
import DropDown from "../../../components/form/DropDown";
import Pagination from "../../../components/Pagination";
import DateRangeCalendar from "../../../components/form/DateRangeCalendar";
import CalendarButton from "../../../components/form/CalendarButton";
// Store and Services
import { getArbitrageBetBookmakers } from "../../../services/arbitrageBetService";
import { fetchArbitrageBets, refresh, setFilters, setPage, setWeek, setDateRange } from "../../../redux/slices/arbitrageBetSlice";
// Helpers
import {
  getWeeks,
  formatDate,
  formatDateTime,
  formatMoney
} from "../../../utils/helpers";

export default function ArbitrageBets() {
  const dispatch = useDispatch();
  const {arbitrageBets, pagination, loading, filters, page} = useSelector((state) => state.arbitrageBet);
  const [bookmakers, setBookmakers] = useState([]);
  const [weeks, setWeeks] = useState([]);
  
  useEffect(() => {

    setWeeks(getWeeks());

    getArbitrageBetBookmakers().then((res) => {
      setBookmakers(res.data.data);
    });

  }, []);

  useEffect(() => {
    dispatch(fetchArbitrageBets());
  }, [dispatch, filters, page]);


  const formik = useFormik({
    initialValues: filters,
    validationSchema: Yup.object({
      bookmakers: Yup.string().nullable(),
      start_date: Yup.date().nullable(),
      end_date: Yup.date().nullable(),
    }),
    onSubmit: (values) => {
      dispatch(setFilters(values));
    },
  });

  const handleClearFilter = () => {
    formik.resetForm();
    dispatch(refresh());
  };

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className="grid grid-cols-10 gap-1 items-center">
          <div className="col-span-2">
            <DropDown  
              name="bookmaker"
              value={formik.values.bookmaker}
              options={bookmakers}
              optionValue="value"
              optionLabel="label"
              placeholder="Select Bookmaker"
              handleChange={(e) =>
                formik.setFieldValue("bookmaker", e.target.value)
              }
            />
          </div>
          
          <div className="col-span-1 flex justify-center">
            <button
              type="submit"
              className="bg-primary px-2 py-1 mr-2 text-white rounded"
            >
              <FontAwesomeIcon icon={faSearch} />
            </button>
            <button
              type="button"
              onClick={handleClearFilter}
              className="bg-primary px-2 py-1 text-white rounded"
            >
              <FontAwesomeIcon icon={faArrowRotateRight} />
            </button>
          </div>
          <div className="col-span-6 flex justify-end gap-2">
            <CalendarButton
              btnText="This week"
              handleSubmit={() => dispatch(setWeek(0))}
              selected={filters.week === 0}
            />
            <CalendarButton
              btnText="Last week"
              handleSubmit={() => dispatch(setWeek(1))}
              selected={filters.week === 1}
            />
            <CalendarButton
            btnText={"Two weeks ago"}
            handleSubmit={() => dispatch(setWeek(2))}
            selected={filters.week == 2}
          />
            <DropDown
              name="week"
              value={filters.week}
              options={weeks}
              optionValue="key"
              optionLabel="value"
              placeholder="Select Week"
              handleChange={(e) => dispatch(setWeek(e.target.value))}
            />
            <DateRangeCalendar
              onSelect={(e) => dispatch(setDateRange(e))}
              start_date={filters.start_date}
              end_date={filters.end_date}
            />
          </div>
        </div>
      </form>

      <hr className="border-black border-2 mt-2" />
      {loading ? (
        <TableLoader />
      ) : (
        <>
        <table className="table-auto w-full border-collapse mt-4 text-sm">
          <thead>
            <tr>
              <th>Created</th>
              <th>Bookmaker</th>
              <th>Sport</th>
              <th>League</th>
              <th>Game ID</th>
              <th>Game Date</th>
              <th>Match</th>
              <th>Bet Type</th>
              <th>Team No</th>
              <th>Team Name</th>
              <th>Odds</th>
              <th>Stake</th>   
            </tr>
          </thead>

          <tbody>
            {arbitrageBets.map((item, index) => {
              return (
                <tr
                  key={index}
                  className="overflow-hidden whitespace-nowrap text-center border border-b border-t-0 border-r-0 border-l-0"
                >
                  <td className="p-2">
                    {formatDateTime(item?.created_at)}
                  </td>
                  <td className="p-2">{item?.bookmaker}</td>
                  <td className="p-2">{item?.sport}</td>
                  <td className="p-2">{item?.league || "-"}</td>
                  <td className="p-2">{item?.game_id}</td>
                  <td className="p-2">
                    {formatDate(item?.game_datetime) }
                  </td>
                  <td className="p-2">
                    <span className="text-xs">
                      {item?.team_1} vs {item?.team_2}
                    </span>
                  </td>
                  <td className="p-2 capitalize">{item?.bet_type}</td>
                  <td className="p-2">{item?.team_no}</td>
                  <td className="p-2">{item?.team_name}</td>
                  <td className="p-2">
                    {item?.odds ? Number(item.odds).toFixed(2) : "-"}
                  </td>
                  <td className="p-2">
                    {formatMoney(item?.stake)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <Pagination pagination={pagination} onPageChange={(page) => dispatch(setPage(page))} />
        </>
      )}
    </div>
  );
}
